import {
  CircleIcon,
  EyeOpenIcon,
  GlobeIcon,
  LoopIcon,
  UpdateIcon,
} from '@radix-ui/react-icons';
import { Box, Card, Flex, IconButton } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { TVHelper } from 'classes/helpers/traj-visualizer.helper';
import { SpeedControlView } from 'components/common/animation/speed-ctrl';
import { ErrorBoundary } from 'components/common/error-boundary';
import {
  BEHIND_MOUND_FT,
  BEHIND_PLATE_FT,
  EYE_LEVEL_FT,
  FPS,
  Perspective,
  PLATE_LEVEL_FT,
  POINT_OFFSETS,
  POV_OPTIONS,
  SPEEDS,
} from 'enums/traj-visualizer';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { IVerboseComponent } from 'interfaces/i-components';
import { ITVDefinition } from 'interfaces/traj-visualizer/i-definition';
import { ITVSceneBall } from 'interfaces/traj-visualizer/i-scene-ball';
import { VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import { RADIX, RadixBtnVariant, RadixColor } from 'lib_ts/enums/radix-ui';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { clone, isNil } from 'lodash';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  ACESFilmicToneMapping,
  Euler,
  Object3D,
  PerspectiveCamera,
  Quaternion,
  sRGBEncoding,
  Event as ThreeEvent,
  Vector3,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

const COMPONENT_NAME = 'TrajectoryView';

const BTN_COLOR: RadixColor = RADIX.COLOR.NEUTRAL;
const BTN_VARIANT: RadixBtnVariant = 'soft';

interface IProps {
  strikeZone?: IStrikeZone;

  // update this to restart the animation
  animateKey?: number;

  definitions: ITVDefinition[];

  /** whether the user can click and drag or zoom the camera */
  showCameraControls?: boolean;

  /** whether the user can change speed or play/pause the animation */
  showSpeedControls?: boolean;

  /** whether the user can cycle through POVs */
  showPovControls?: boolean;

  /** whether the ball spins and whether the user can toggle it */
  showSpinControls?: boolean;

  /** default will be batter (i.e. behind home plate, facing towards mound) */
  startingPOV?: Perspective;

  startingZoom?: number;

  aspectRatio?: string;
}

export const TrajectoryView: FC<IVerboseComponent & IProps> = (props) => {
  const node = useRef<HTMLDivElement>(null);
  const renderer = useRef(new WebGLRenderer({ antialias: true }));
  const camera = useRef(
    new PerspectiveCamera(
      45,
      node.current
        ? node.current.clientWidth / node.current.clientHeight
        : undefined,
      1,
      1000
    )
  );

  // nil value => yet to init or is in progress
  const [animateKey, setAnimateKey] = useState<number>();

  // whenever parent changes props.animateKey, update local animateKey
  useEffect(() => setAnimateKey(Date.now()), [props.animateKey]);

  const [controls, setControls] = useState<OrbitControls>();

  const scene = useRef(new Object3D<ThreeEvent>());

  const lastTime = useRef(0);

  const [cameraPov, setCameraPov] = useState<Perspective>(
    props.startingPOV ?? 'batter'
  );

  const [speed, setSpeed] = useState(5);
  const latestSpeed = useLatest(speed);

  // start spinning by default if controls are shown
  const [spinning, setSpinning] = useState(!!props.showSpinControls);
  const latestSpinning = useLatest(spinning);

  const models = useRef<ITVSceneBall[]>([]);

  const latestDefinitions = useLatest(props.definitions);

  const handleWindowResize = useCallback(() => {
    if (!node.current) {
      return;
    }

    camera.current.aspect =
      node.current.clientWidth / node.current.clientHeight;

    camera.current.updateProjectionMatrix();

    renderer.current.setSize(
      node.current.clientWidth,
      node.current.clientHeight
    );
  }, []);

  useEffect(() => {
    verboseDebug(props, 'window resize > started listening');
    window.addEventListener('resize', handleWindowResize);

    return () => {
      verboseDebug(props, 'window resize > stopped listening');
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [handleWindowResize]);

  useEffect(() => {
    if (!controls) {
      return;
    }

    verboseDebug(props, 'controls changed > updating', controls.target);
    controls.update();
  }, [controls]);

  const resetCamera = useCallback(() => {
    verboseDebug(props, `resetCamera > ${cameraPov}`);

    switch (cameraPov) {
      case 'batter': {
        camera.current.position.z = BEHIND_PLATE_FT;
        camera.current.position.y = PLATE_LEVEL_FT;
        camera.current.position.x = 0;
        break;
      }

      case 'pitcher-1': {
        camera.current.position.x = 20;
        camera.current.position.y = EYE_LEVEL_FT;
        camera.current.position.z = BEHIND_MOUND_FT;
        break;
      }

      case 'pitcher-2': {
        camera.current.position.x = 0;
        camera.current.position.y = EYE_LEVEL_FT;
        camera.current.position.z = BEHIND_MOUND_FT;
        break;
      }

      case 'pitcher-3': {
        camera.current.position.x = -20;
        camera.current.position.y = EYE_LEVEL_FT;
        camera.current.position.z = BEHIND_MOUND_FT;
        break;
      }

      default: {
        break;
      }
    }

    // reset controls
    setControls((prev) => {
      const next = clone(prev);

      if (!next) {
        return prev;
      }

      // point camera back at the strike zone
      next.target = new Vector3(0, PLATE_LEVEL_FT, 0);

      return next;
    });
  }, [cameraPov]);

  // whenever cameraPov changes, reset the camera
  const latestResetCamera = useLatest(resetCamera);

  useEffect(() => {
    latestResetCamera.current();
  }, [cameraPov]);

  // restart animation interval
  useEffect(() => {
    if (isNil(animateKey)) {
      // waiting for first init
      return;
    }

    verboseDebug(props, `restartAnimation > ${models.current.length} balls`);

    // restart the animation for each model
    models.current.forEach((m) => {
      /** reset points */
      m.points = POINT_OFFSETS.map(() => []);

      /** remove old lines */
      m.lines.forEach((l) => scene.current.remove(l));

      /** calculate starting position */
      const source = latestDefinitions.current.find(
        (p) => p.guid === m.definition.guid
      );

      if (!source) {
        console.warn(
          `${COMPONENT_NAME}: unable to locate pitch for model with GUID "${m.definition.guid}"`
        );
        return;
      }

      /** quaternion representing starting orientation */
      const oQuat = new Quaternion(
        source.bs.qx,
        source.bs.qy,
        source.bs.qz,
        source.bs.qw
      );

      /** euler object representing starting orientation */
      const oEuler = new Euler().setFromQuaternion(oQuat);

      /** reset visibility */
      m.group.visible = true;

      m.group.position.x = source.traj.px;
      m.group.position.y = source.traj.pz;
      m.group.position.z = -source.traj.py;

      m.group.rotation.x = oEuler.x;
      m.group.rotation.y = oEuler.z;
      m.group.rotation.z = -oEuler.y;

      m.timestampSec = 0;
      // * no need to reset arrivalDeltaMs since that has not changed

      m.localTraj.px = source.traj.px;
      m.localTraj.py = source.traj.pz;
      m.localTraj.pz = -source.traj.py;

      /** todo: confirm we can remove the multiplied factor of 1.46667 (bias?) */
      m.localTraj.vx = source.traj.vx;
      m.localTraj.vy = source.traj.vz;
      m.localTraj.vz = source.traj.vy;

      m.localTraj.ax = source.traj.ax;
      m.localTraj.ay = source.traj.az;
      m.localTraj.az = -source.traj.ay;

      m.localBs.wx = source.bs.wx;
      m.localBs.wy = source.bs.wz;
      m.localBs.wz = -source.bs.wy;
    });

    /** ensure that the animation timing is based on the moment before the interval first runs */
    lastTime.current = performance.now();

    verboseDebug(props, 'restartAnimation > starting animation');
    const interval = setInterval(() => {
      const now = performance.now();

      const deltaSec = (now - lastTime.current) / 1_000; // time since the last run

      // remove lines for hidden balls
      models.current
        .filter((ball) => !ball.group.visible)
        .forEach((ball) => ball.lines.forEach((l) => scene.current.remove(l)));

      const ballsInTransit = models.current.filter(
        (ball) => ball.group.visible && ball.timestampSec < ball.arrivalSec
      );

      // * do not cancel the interval even if there are no balls in transit b/c it would also stop panning/zooming/etc...

      ballsInTransit.forEach((ball) => {
        /** iterate ball spin and position */
        TVHelper.moveBall({
          ball: ball,
          deltaSec: deltaSec,
          animationSpin: latestSpinning.current,
          animationSpeed: latestSpeed.current / SPEEDS.MAX,
          verbose: props.verbose,
        });

        /** iterate ball tracers */
        TVHelper.traceBall(ball, scene.current);
      });

      lastTime.current = now;

      renderer.current.render(scene.current, camera.current);
    }, 1_000 / FPS);

    return () => {
      verboseDebug(props, 'restartAnimation > stopping animation');
      clearInterval(interval);
    };
  }, [props.verbose, animateKey]);

  // initialize the scene
  const initScene = useCallback(
    async (definitions: ITVDefinition[]) => {
      verboseDebug(props, 'initScene > started');

      if (!node.current) {
        console.warn(`${COMPONENT_NAME}: node was not defined`);
        return;
      }

      setAnimateKey(undefined);

      const newScene = await TVHelper.createScene();

      if (!newScene) {
        console.warn(`${COMPONENT_NAME}: scene failed to load`);
        return;
      }

      verboseDebug(props, 'initScene > newScene created');

      /** load additional items from elements module */
      TVHelper.addTextures(newScene);

      verboseDebug(props, 'initScene > textures added');

      await TVHelper.addStrikeZone(
        newScene,
        props.strikeZone ?? VISIBLE_STRIKEZONE
      );

      verboseDebug(props, 'initScene > strike zone added');

      models.current = await Promise.all(
        definitions.map((m) => TVHelper.addBall(m, newScene))
      );

      verboseDebug(props, 'initScene > balls added');

      /** set scene for use by the rest of the component */
      scene.current = newScene;

      // renderer.current = new WebGLRenderer({ antialias: true });
      renderer.current.setPixelRatio(window.devicePixelRatio);
      renderer.current.setSize(window.innerWidth, window.innerHeight);
      renderer.current.toneMapping = ACESFilmicToneMapping;
      renderer.current.toneMappingExposure = 1;
      renderer.current.outputEncoding = sRGBEncoding;
      renderer.current.setClearColor(0x000000, 0);

      node.current.appendChild(renderer.current.domElement);

      /** add lighting - doesn't do anything? */
      // scene.current.add(new AmbientLight(0xffffff, 1.0));

      // force things to get sized properly
      handleWindowResize();

      const controls = new OrbitControls(
        camera.current,
        renderer.current.domElement
      );

      // disable panning/zooming/etc... when controls aren't shown
      const enabled = !!props.showCameraControls;
      const controlSpeed = 0.2;

      controls.enablePan = enabled;
      controls.panSpeed = controlSpeed;

      controls.enableZoom = enabled;
      controls.zoomSpeed = controlSpeed;

      controls.enableDamping = enabled;
      controls.dampingFactor = controlSpeed;

      controls.enableRotate = enabled;
      controls.rotateSpeed = controlSpeed;

      controls.target = new Vector3(0, PLATE_LEVEL_FT, 0);

      setControls(controls);

      setAnimateKey(Date.now());

      verboseDebug(props, 'initScene > finished');
    },
    [
      props.strikeZone,
      props.showCameraControls,
      handleWindowResize,
      resetCamera,
    ]
  );

  const latestInitScene = useLatest(initScene);

  // whenever definitions change, re-init the whole scene
  useEffect(() => {
    verboseDebug(props, 'initScene triggered > definitions changed');
    latestInitScene.current(props.definitions);
  }, [props.definitions]);

  const hasOptionalControls =
    props.showSpinControls || props.showPovControls || props.showSpeedControls;

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <Flex
        direction="column"
        gap={RADIX.FLEX.GAP.SM}
        height="100%"
        width="100%"
      >
        {hasOptionalControls && (
          <Flex gap={RADIX.FLEX.GAP.XS} justify="between">
            <Flex gap={RADIX.FLEX.GAP.XS}>
              {props.showSpinControls && (
                <IconButton
                  color={BTN_COLOR}
                  variant={BTN_VARIANT}
                  title={t('common.toggle-ball-spin-animation').toString()}
                  onClick={() => setSpinning((prev) => !prev)}
                >
                  {spinning ? <GlobeIcon /> : <CircleIcon />}
                </IconButton>
              )}

              {props.showPovControls && (
                <IconButton
                  color={BTN_COLOR}
                  variant={BTN_VARIANT}
                  title={t('common.change-camera-perspective').toString()}
                  onClick={() => {
                    const nextIndex =
                      POV_OPTIONS.findIndex((p) => p === cameraPov) + 1;
                    setCameraPov(POV_OPTIONS[nextIndex % POV_OPTIONS.length]);
                  }}
                >
                  <EyeOpenIcon />
                </IconButton>
              )}
            </Flex>

            {props.showSpeedControls && (
              <SpeedControlView
                speed={speed}
                onChange={(value) => setSpeed(value)}
              />
            )}
          </Flex>
        )}

        <Card
          style={{
            padding: 0,
            aspectRatio: props.aspectRatio,
            position: 'relative',
          }}
          className="rt-VertFlexGrow"
        >
          {props.showCameraControls && (
            <Box position="absolute" top="2" left="2" style={{ zIndex: 1 }}>
              <IconButton
                color={BTN_COLOR}
                variant={BTN_VARIANT}
                title={t('common.reset-camera-position').toString()}
                onClick={() => resetCamera()}
              >
                <UpdateIcon />
              </IconButton>
            </Box>
          )}

          <Box position="absolute" top="2" right="2" style={{ zIndex: 1 }}>
            <IconButton
              color={BTN_COLOR}
              variant={BTN_VARIANT}
              title={t('common.replay-animation').toString()}
              onClick={() => setAnimateKey(Date.now())}
            >
              <LoopIcon />
            </IconButton>
          </Box>
          <div
            ref={node}
            style={{
              height: props.aspectRatio ? '100%' : '400px',
              borderRadius: '5px',
              overflow: 'hidden',
              backgroundColor: '#000000',
            }}
          />
        </Card>
      </Flex>
    </ErrorBoundary>
  );
};
